import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import App from '@/App.vue'
import router from '@/router'
import '@/assets/fonts.css'
import '@/assets/main.css'
import '@/assets/experience.css'
import '@/assets/editorial.css'
import '@/assets/learning.css'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import * as Sentry from '@sentry/vue'
import { useToast } from 'vue-toastification'
import { getApiBase } from '@/utils/url'
import { useThemeStore } from '@/stores/theme'
import { getConsent } from '@/utils/cookies'
import { prefetchCoursesOnStartup } from '@/startup/prefetch'

const app = createApp(App)
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

if (import.meta.env.VITE_SENTRY_DSN) {
  const apiBase = getApiBase()
  const consent = getConsent()
  let allowAnalytics = consent.analytics === true
  const traces = allowAnalytics ? Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1) : 0
  const replaysSession = allowAnalytics ? Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0.0) : 0
  const replaysOnError = allowAnalytics ? Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 1.0) : 0
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      // Only meaningful if consent is granted (rates set to 0 otherwise)
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })
    ],
    // Route events through backend to avoid adblockers
    tunnel: apiBase + 'sentry-tunnel/',
    tracesSampleRate: traces,
    // Session Replay sampling (gated by consent)
    replaysSessionSampleRate: replaysSession,
    replaysOnErrorSampleRate: replaysOnError,
    // Drop events entirely if analytics consent is not granted
    beforeSend(event) {
      try { return allowAnalytics ? event : null } catch (_) { return null }
    },
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined
  })

  // Update consent dynamically without re-initializing Sentry
  window.addEventListener('cookie-consent-changed', (e) => {
    try { allowAnalytics = e.detail?.analytics === true } catch (_) { allowAnalytics = false }
  })
}

// Toast configuration
app.use(Toast, {
  position: 'top-right',
  timeout: 8000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: 'button',
  icon: true,
  rtl: false,
  transition: 'Vue-Toastification__bounce',
  maxToasts: 5,
  newestOnTop: true
})

app.config.errorHandler = (err) => {
  try {
    const toast = useToast()
    toast.error('An unexpected error occurred. Please try again.')
    Sentry.captureException(err)
  } catch (_) {}
}

app.use(pinia)

// Prefetch public courses list in the background on startup to hide free-tier latency.
prefetchCoursesOnStartup(pinia)

try {
  const theme = useThemeStore()
  theme.init()
} catch (_) {}

app.use(router).mount('#app')

